import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Check, Settings, X } from 'lucide-react';
import { useTranslate } from '../context/LanguageContext';

export default function CookieBanner() {
  const { language } = useTranslate();
  const isAr = language === 'ar';
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const saved = localStorage.getItem('user_privacy_cookies');
    if (!saved) {
      // Small delay to not overwhelm the user immediately
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem(
      'user_privacy_cookies',
      JSON.stringify({ necessary: true, analytics: true, personalization: true })
    );
    setShowBanner(false);
  };

  const handleDecline = () => {
    localStorage.setItem(
      'user_privacy_cookies',
      JSON.stringify({ necessary: true, analytics: false, personalization: false })
    );
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 100 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 pointer-events-none"
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className="max-w-5xl mx-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5 sm:p-6 rounded-2xl shadow-2xl pointer-events-auto flex flex-col lg:flex-row gap-6 items-start lg:items-center">
          
          <div className="flex-1 flex gap-4">
            <div className="w-12 h-12 bg-red-50 dark:bg-red-500/10 rounded-xl flex items-center justify-center shrink-0">
              <ShieldAlert className="text-red-600 dark:text-red-500" size={24} />
            </div>
            <div>
              <h3 className="font-black text-lg text-zinc-900 dark:text-white mb-1">
                {isAr ? 'نحن نستخدم ملفات تعريف الارتباط' : 'We use cookies'}
              </h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed">
                {isAr 
                  ? 'نستخدم ملفات تعريف الارتباط (الكوكيز) لتحسين تجربتك على موقعنا، وتحليل حركة الزوار، وتخصيص المحتوى. باختيارك "قبول الكل"، فإنك توافق على استخدامنا لجميع ملفات تعريف الارتباط.'
                  : 'We use cookies to improve your experience on our site, analyze traffic, and personalize content. By clicking "Accept All", you consent to our use of all cookies.'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto shrink-0">
            <a 
              href="/privacy-policy" 
              onClick={() => setShowBanner(false)}
              className="px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 text-sm font-bold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex items-center gap-2"
            >
              <Settings size={16} />
              {isAr ? 'تخصيص' : 'Preferences'}
            </a>
            <button 
              onClick={handleDecline}
              className="px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 text-sm font-bold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors flex items-center gap-2"
            >
              <X size={16} />
              {isAr ? 'الرفض' : 'Decline'}
            </button>
            <button 
              onClick={handleAcceptAll}
              className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-all shadow-lg shadow-red-600/20 flex items-center gap-2"
            >
              <Check size={16} />
              {isAr ? 'قبول الكل' : 'Accept All'}
            </button>
          </div>

        </div>
      </motion.div>
    </AnimatePresence>
  );
}
